/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/member-ordering */
import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CurrencyConverterPurePipe } from 'app/shared/currency-converter-pure.pipe';
import { parkBrand } from 'app/shared/modals/modal-hotel/park-flow/park-display';

@Component({
  selector: 'usb-base-item',
  templateUrl: './base-item.component.html',
  styleUrls: ['./base-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseItemComponent implements OnInit {
  constructor(protected cdr: ChangeDetectorRef, protected currency: CurrencyConverterPurePipe) {}

  // State
  isExpanded = false;
  displayPrice: number = 0;
  @Output() openMap = new EventEmitter<any>();

  airlineLogo = '';

  ngOnInit(): void {
    // Parques: el detalle (habitación, tickets, plan de comidas) se muestra abierto.
    this.isExpanded = this.item?.categoryId === 4;
    this.getStarsHotels();
    this.computeDisplayPrice();
    this.cdr.markForCheck();

    if (this.item?.categoryId === 5) {
      this.getAirlineLogo();
    }
  }

  private computeDisplayPrice(): void {
    if (this.item?.statusId === 5) {
      this.displayPrice = 0;
      return;
    }

    if((this.item?.categoryId === 1 || this.item?.categoryId === 4)) {
      this.displayPrice = this.item?.totalPrice;
      return;
    }

    // Vuelos: es un único producto (una sola tarifa para todo el itinerario,
    // ida y vuelta incluidos) recién creado en el front — todavía no tiene
    // netPriceAgent (lo calcula el backend al persistir). Usamos pricing.totalPriceProf,
    // que ya viene con la rentabilidad aplicada en la moneda propia del vuelo.
    if (this.item?.categoryId === 5) {
      this.displayPrice = this.currency.transform(
        this.item?.totalPrice,
        this.item?.currency,
        this.currencyAgency?.code,
        this.currencies
      ) || 0;
      return;
    }

    // Traslados (ida y vuelta): el item padre trae solo un netPriceAgent,
    // hay que sumar cada transfer (excluyendo cancelados) con la misma
    // lógica de conversión que usa transport-item para mostrarlos.
    if (this.item?.categoryId === 8 && Array.isArray(this.item?.transfers) && this.item.transfers.length > 0) {
      this.displayPrice = this.item.transfers.reduce((acc: number, t: any) => {
        if (t?.statusId === 5 || t?.status_id === 5) return acc;
        const price = (this.item?.isPaidAgency || this.item?.isPaidProvider)
          ? (t?.convertedAmounts?.netPriceAgent || t?.netPrice || 0)
          : (this.currency.transform(
              t?.netPriceAgent,
              t?.currency,
              this.currencyAgency?.code,
              this.currencies
            ) || 0);
        return acc + price;
      }, 0);
      return;
    }

    if (this.item?.isPaidAgency || this.item?.isPaidProvider) {
      this.displayPrice = this.item?.convertedAmounts?.netPriceAgent || this.item?.netPrice || 0;
      return;
    }
    this.displayPrice = this.currency.transform(
      this.item?.netPriceAgent,
      this.item?.currency,
      this.currencyAgency?.code,
      this.currencies
    ) || 0;
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    this.cdr.markForCheck();
  }

  onOpenMap(): void {
    this.openMap.emit(this.item);
  }

  @Input() item: any;
  @Input() styleSidebar: any;
  @Input() currencyAgency: any;
  @Input() currencies: any;
  @Input() clientsSettingAppBooking: any;

  // Constants
  rate: number = 0;
  starArray: number[] = [1, 2, 3, 4, 5];

  getStarsHotels(): void {
    // Just for hotel category
    const rateStar = this.item?.categoryHotel;

    if(rateStar !== null) {
      const numberRate = String(rateStar ?? "").substring(0,1);
      this.rate = Number(numberRate);
    }
  }

  getCategoryIcon(): string {
    // Ticket
    if(this.item.subCategoryId === 9) return "../../../assets/img/app/categories-search-box/combi-ticket.svg";

    // Insurance
    if(this.item.categoryName === 'INSURANCE') return '../../../assets/img/app/categories-search-box/icon-seguro.svg';

    const iconMap: { [key: string]: string } = {
      "HOTELS": '../../../assets/img/app/categories-search-box/ima_hotel.svg',
      "TRANSPORTS": '../../../assets/img/app/categories-search-box/bus.svg',
      "RENT-A-CAR": '../../../assets/img/app/categories-search-box/car-compact.svg',
      "TOURS": '../../../assets/img/app/categories-search-box/road-map.svg',
      "FLIGHTS": '../../../assets/img/app/categories-search-box/AEREO.svg',
      "BOATS": '../../../assets/img/app/categories-search-box/boat.svg',
      "PARKS": '../../../assets/img/app/categories-search-box/parks.svg'
    };
    return iconMap[this.item?.categoryName] || '';
  }

  getStatusClass(): string {
    const statusMap: { [key: number]: string } = {
      1: 'product-status-saved',
      2: 'product-status-reserved',
      3: 'product-status-locked',
      4: 'product-status-denied',
      5: 'product-status-cancelled',
      6: 'product-status-paid-agency',
      7: 'product-status-paid-provider',
      8: 'product-status-completed'
    };
    return statusMap[this.item?.statusId] || '';
  }

  isRemovable(): boolean {
    return this.item?.statusId === 1 || this.item?.statusId === 4;
  }

  formatDate(date: string): string {
    if (!date) return '';
    const parts = date.split('-');
    if (parts.length !== 3) return date;
    const day = parts[2].substring(0, 2);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${monthNames[parseInt(parts[1], 10) - 1]}`;
  }

  stringUpperCase(str: string): string {
    if (typeof str !== 'string' || !str) return '';
    return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  getDisplayPrice(): number {
    if (this.item?.isPaidAgency || this.item?.isPaidProvider) {
      return this.item?.convertedAmounts?.netPriceAgent || this.item?.netPrice || 0;
    }
    const price = this.currency.transform(this.item?.netPriceAgent,
                                          this.item?.currency,
                                          this.currencyAgency?.code,
                                          this.currencies
                                          )
    return price || 0;
  }

  getOriginalCurrencyPrice(): number {
    // Vuelos: mismo criterio que computeDisplayPrice, en la moneda propia del vuelo.
    if (this.item?.categoryId === 5) {
      return this.item?.totalPrice ?? 0;
    }
    // Traslados (ida y vuelta): sumar el netPriceAgent de cada transfer
    // (en su moneda original, sin convertir). Cancelled (status 5) se excluye.
    if (this.item?.categoryId === 8 && Array.isArray(this.item?.transfers) && this.item.transfers.length > 0) {
      return this.item.transfers.reduce((acc: number, t: any) => {
        if (t?.statusId === 5 || t?.status_id === 5) return acc;
        return acc + (t?.netPriceAgent || 0);
      }, 0);
    }
    return this.item?.netPriceAgent || 0;
  }

  /**
   * Para traslados: devuelve un array con los netPriceAgent agrupados por moneda.
   * Si todos los transfers comparten la misma moneda (caso común ida/vuelta),
   * devuelve un único elemento. Si hay monedas mixtas, devuelve varios elementos
   * para que el template pueda renderizar una línea por moneda.
   */
  getOriginalCurrencyBreakdown(): Array<{ currency: string; total: number }> {
    if (this.item?.categoryId !== 8 || !Array.isArray(this.item?.transfers) || this.item.transfers.length === 0) {
      return [];
    }
    const map = new Map<string, number>();
    for (const t of this.item.transfers) {
      if (t?.statusId === 5 || t?.status_id === 5) continue;
      const cur = t?.currency || this.item?.currency || '';
      const price = t?.netPriceAgent || 0;
      map.set(cur, (map.get(cur) || 0) + price);
    }
    return Array.from(map.entries()).map(([currency, total]) => ({ currency, total }));
  }

  hasOriginalPriceDiff(): boolean {
    return this.item?.currency !== this.currencyAgency?.code && this.item?.statusId !== 5;
  }

  getCategoryLabel(): string {
    if (this.item?.categoryId === 6) return 'TOURS';
    if (this.item?.categoryId === 9) return 'TICKET';
    if (this.item?.categoryId === 11) return 'INSURANCE';
    return this.item?.categoryName || '';
  }

  getCategoryType(): string {
    const categoryType: {
      [ key: number ]: string
    } = {
      1: 'Hotel',
      2: 'Mediano',
      5: 'Vuelo',
      6: 'Experiencia',
      9: 'Ticket',
      8: 'Traslado',
      10: 'Asistencia',
      11: 'Insurance',
      3: 'Embarcación',
      4: 'Parque Temático'
    }

    if (this.item?.categoryId === 4) {
      const brand = this.getParkBrand();
      return brand ? `Parque Temático ${brand === 'UNIVERSAL' ? 'Universal' : 'Disney'}` : 'Parque Temático';
    }

    return categoryType[this.item?.categoryId] || '';
  }

  /**
   * Parques: parque de la compra (DISNEY / UNIVERSAL). El grupo del carrito no lo trae; sale del primer producto
   * (búsqueda original) y, como último recurso, del nombre del hotel.
   */
  getParkBrand(): string {
    return parkBrand(this.item);
  }

  /** Parques: el grupo del carrito no trae la clase de tarifa; se toma de la habitación comprada. */
  parkRateClass(): string {
    if (this.item?.categoryId !== 4) {
      return '';
    }
    const room = this.item?.rooms?.[0] || {};
    const rate = [room.rateClassId, room.rateClass, room.pricing?.rateClass, room.room?.rateClass].find(r => r === 'R' || r === 'NR');
    return rate || '';
  }

  getTypeWayLabel(): string {
    if (this.item?.firstWay !== undefined) {
      return this.item.firstWay === 1 ? 'IDA' : 'VUELTA';
    }
    if (this.item?.noCloneFirstWay !== undefined) {
      return this.item.noCloneFirstWay === 1 ? 'IDA' : 'VUELTA';
    }
    return '';
  }

  isRefundable(): boolean {
    return this.item?.rateClass === 'R';
  }

  /**
   * Indica si el item está cancelado (status 5).
   *
   * Soporta DOS formatos de status:
   *  1. Items con varias habitaciones (ej. hotel): vienen con un array `statusIds: [{ statusId, statusName }]`
   *     En este caso, el item está cancelado SOLO si TODAS las habitaciones tienen status 5.
   *  2. Items simples: vienen con un `statusId` (camelCase) o `status_id` (snake_case) directo.
   */
  isCancelled(): boolean {
    // Caso 1: array de statusIds (varias habitaciones, ej. hotel)
    if (this.item?.statusIds && Array.isArray(this.item.statusIds) && this.item.statusIds.length > 0) {
      // Está cancelado si TODAS las habitaciones están en status 5
      return this.item.statusIds.every((s: any) => s?.statusId === 5);
    }

    // Caso 2: statusId directo (acepta camelCase y snake_case)
    const statusId = this.item?.statusId ?? this.item?.status_id;
    return statusId === 5;
  }


  // Devuelve la option realmente reservada del leg (matchea selectedOptions,
  // con fallback a options[0] si no hay match) — mismo criterio para todos
  // los helpers de vuelo de acá abajo, así no cada uno busca distinto.
  private getSelectedFlightOption(legIndex: number = 0): any {
    const leg = this.item?.itinerary?.[legIndex];
    const optionId = this.item?.selectedOptions?.[legIndex] ?? this.item?.selectedOptions?.[0];
    return leg?.options?.find((option: any) => option.optionId === optionId) ?? leg?.options?.[0] ?? null;
  }

  private getFlightSegments(legIndex: number = 0): any[] {
    return this.getSelectedFlightOption(legIndex)?.segments || [];
  }

  getFlightAirlineName(): string {
    return this.getFlightSegments()[0]?.airline?.name || '';
  }

  // Ciudad + IATA de cada escala (todos los segmentos salvo el último, cuyo
  // arrival ya es el destino final, no una escala).
  getFlightStops(): { city: string; airport: string }[] {
    const segments = this.getFlightSegments();
    if (segments.length <= 1) {
      return [];
    }
    return segments.slice(0, -1).map((segment: any) => ({
      city: segment?.arrival?.city,
      airport: segment?.arrival?.airport,
    }));
  }

  // Prefiere segment.layover (minutos, ya lo informa la API); si falta,
  // calcula la diferencia real entre el arrival del segmento y el departure
  // del siguiente (eso resuelve solo la diferencia horaria entre aeropuertos).
  getFlightLayoverMinutes(): number {
    const segments = this.getFlightSegments();
    let total = 0;
    for (let i = 0; i < segments.length - 1; i++) {
      const current = segments[i];
      if (current?.layover != null) {
        total += current.layover;
        continue;
      }
      const next = segments[i + 1];
      if (current?.arrival?.dateTime && next?.departure?.dateTime) {
        const arrival = new Date(current.arrival.dateTime).getTime();
        const departure = new Date(next.departure.dateTime).getTime();
        total += Math.max(0, Math.round((departure - arrival) / 60000));
      }
    }
    return total;
  }

  getTripTypeAndPax(): string {
    const tripType = (this.item?.itinerary?.length || 0) > 1 ? 'Ida y Vuelta' : 'Solo ida';
    const adults = this.item?.adults || 0;
    const children = this.item?.children || 0;
    const parts = [`${adults} ${adults === 1 ? 'adulto' : 'adultos'}`];
    if (children > 0) {
      parts.push(`${children} ${children === 1 ? 'Niño' : 'Niños'}`);
    }
    return `${tripType} - ${parts.join(', ')}`;
  }

  private toTitleCase(value: string): string {
    if (!value) {
      return '';
    }
    return value.toLowerCase().replace(/\b\w/g, (letter: string) => letter.toUpperCase());
  }

  getItemOriginDeparture(): string {
    const segments = this.getFlightSegments();
    const first = segments[0];
    const last = segments[segments.length - 1];
    if (!first || !last) {
      return this.item?.title || '';
    }
    const origin = first.departure?.city || first.departure?.airport;
    const destination = last.arrival?.city || last.arrival?.airport;
    return `${this.toTitleCase(origin)} - ${this.toTitleCase(destination)}`;
  }

  getAirlineLogo(): void {
    this.airlineLogo = this.getFlightSegments()[0]?.airline?.operatingLogo || '';
  }

  getDateInfo(action: string): string {

    let date = '';
    const segments = this.getFlightSegments();
    const lastSegment = segments[segments.length - 1];

    switch(action) {

      // dates
      case 'departureInitial':
        date = segments[0]?.departure?.dateTime;
        break;
      case 'arrivalDestination':
        date = lastSegment?.arrival?.dateTime;
        break;

      // city
      case 'departureCity':
        date = segments[0]?.departure?.city;
        break;
      case 'arrivalDestinationCity':
        date = lastSegment?.arrival?.city;
        break;

      // iata
      case 'departureAirportIata':
        date = segments[0]?.departure?.airport;
        break;
      case 'arrivalDestinationAirportIata':
        date = lastSegment?.arrival?.airport;
        break;

      // cantidad segmento
      case 'segmentQty': {
        const segmentLength = segments.length;
        if (segmentLength === 2) {
          date = '1 escala';
        } else if (segmentLength > 2) {
          date = `${segmentLength - 1} escalas`;
        } else {
          date = 'Directo';
        }
        break;
      }

        // duración de vuelo
      case 'durationFlight':
        date = this.formatDuration(this.getSelectedFlightOption()?.duration);
        break;

        // clase
      case 'class':
        date = segments[0]?.cabin?.category;

    }

    return date;
  }

    formatDuration(minutes: number): string {
    if (!minutes) {
      return '';
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

    // Vuelo elegido "Por tramos": el item es un producto de UN solo tramo, cuyo
  // itinerary.legNumber siempre arranca en 1 (es una búsqueda one-way nueva),
  // así que no sirve para saber si es ida o vuelta — para eso está
  // item.direction (tag puesto por FlyProductService). "Vuelo completo"
  // (item sin direction) sigue usando legNumber como antes.
  getDirectionLabel(leg: any): string {
    if (this.item?.direction) {
      if (this.item.direction === 'IDA') return 'Ida';
      if (this.item.direction === 'VUELTA') return 'Vuelta';
      return this.item.direction;
    }
    return leg?.legNumber === 1 ? 'Ida' : 'Vuelta';
  }
}
